import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    RiArrowLeftLine,
    RiCheckLine,
    RiAlertLine,
    RiBookOpenLine,
    RiRadarLine,
    RiBarChartLine,
    RiLoader4Line,
    RiUser3Line,
    RiRobot2Line,
    RiTimeLine,
} from 'react-icons/ri';
import { simulationService } from '../../services/simulationService';
import type { SimulationDetail, SimulationReport } from '../../services/simulationService';
import GlassCard from '../../components/ui/v2/GlassCard';
import GlassButton from '../../components/ui/v2/GlassButton';

// ---------------------------------------------------------------------------
// Score Ring (SVG circle)
// ---------------------------------------------------------------------------
function ScoreRing({ score, size = 120, strokeWidth = 8 }: { score: number; size?: number; strokeWidth?: number }) {
    const r = (size - strokeWidth) / 2;
    const circ = 2 * Math.PI * r;
    const dash = (score / 100) * circ;

    const color = score >= 80 ? '#4ade80' : score >= 65 ? '#facc15' : '#f87171';

    return (
        <div className="relative" style={{ width: size, height: size }}>
            <svg width={size} height={size} className="-rotate-90">
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="white" strokeOpacity={0.08} strokeWidth={strokeWidth} />
                <motion.circle
                    cx={size / 2} cy={size / 2} r={r}
                    fill="none" stroke={color} strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray={`${dash} ${circ}`}
                    initial={{ strokeDasharray: `0 ${circ}` }}
                    animate={{ strokeDasharray: `${dash} ${circ}` }}
                    transition={{ duration: 1.2, ease: 'easeOut' }}
                />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl font-bold text-white">{score}</span>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Verdict badge colours
// ---------------------------------------------------------------------------
function verdictStyle(verdict: string): string {
    const v = verdict?.toLowerCase() ?? '';
    if (v.includes('strong yes')) return 'bg-green-500/15 text-green-400 border-green-500/30';
    if (v.includes('lean yes')) return 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30';
    if (v.includes('lean no')) return 'bg-orange-500/15 text-orange-400 border-orange-500/30';
    if (v.includes('no')) return 'bg-red-500/15 text-red-400 border-red-500/30';
    return 'bg-white/10 text-white border-white/20';
}

// ---------------------------------------------------------------------------
// Score bar (horizontal)
// ---------------------------------------------------------------------------
function ScoreBar({ label, value }: { label: string; value: number }) {
    const color = value >= 80 ? 'bg-green-400' : value >= 65 ? 'bg-yellow-400' : 'bg-red-400';
    return (
        <div className="space-y-1.5">
            <div className="flex justify-between text-sm">
                <span className="text-gray-300">{label}</span>
                <span className="font-semibold text-white">{value}</span>
            </div>
            <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                <motion.div
                    className={`h-full rounded-full ${color}`}
                    initial={{ width: 0 }}
                    animate={{ width: `${value}%` }}
                    transition={{ duration: 0.9, ease: 'easeOut' }}
                />
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Loading / Polling state
// ---------------------------------------------------------------------------
function AnalysisLoading() {
    return (
        <div className="flex flex-col items-center justify-center h-full gap-6 py-24">
            <div className="relative w-20 h-20">
                <div className="absolute inset-0 rounded-full border-2 border-white/10" />
                <div className="absolute inset-0 rounded-full border-t-2 border-white animate-spin" />
                <RiLoader4Line className="absolute inset-0 m-auto text-2xl text-white/40" />
            </div>
            <div className="text-center space-y-2">
                <p className="text-lg font-semibold text-white">Building your report...</p>
                <p className="text-sm text-gray-500">GPT-4o is analysing your performance. Usually under 30 seconds.</p>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Transcript List
// ---------------------------------------------------------------------------
function TranscriptSection({ transcript }: { transcript: any[] }) {
    if (!transcript || transcript.length === 0) return null;

    return (
        <GlassCard className="p-6 h-fit lg:sticky lg:top-24">
            <div className="flex items-center gap-2 text-white font-semibold mb-6">
                <RiBookOpenLine className="text-gray-400" />
                Conversation Transcript
            </div>
            <div className="space-y-6 max-h-[600px] lg:max-h-[calc(100vh-200px)] overflow-y-auto pr-2 custom-scrollbar">
                {transcript.map((turn, i) => {
                    const isAssistant = turn.role === 'assistant';
                    const timeStr = turn.timestamp_ms ? new Date(turn.timestamp_ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';

                    return (
                        <div key={i} className="space-y-2">
                            <div className="flex items-center justify-between">
                                <div className={`flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest ${isAssistant ? 'text-blue-400' : 'text-purple-400'}`}>
                                    {isAssistant ? <RiRobot2Line size={14} /> : <RiUser3Line size={14} />}
                                    {isAssistant ? 'Interviewer' : 'You'}
                                </div>
                                {timeStr && (
                                    <div className="flex items-center gap-1 text-[10px] text-gray-500">
                                        <RiTimeLine size={10} />
                                        {timeStr}
                                    </div>
                                )}
                            </div>
                            <div className={`p-3 rounded-2xl text-xs leading-relaxed border ${isAssistant ? 'bg-blue-500/5 text-blue-50/90 border-blue-500/10' : 'bg-purple-500/5 text-purple-50/90 border-purple-500/10'}`}>
                                {turn.content}
                            </div>
                        </div>
                    );
                })}
            </div>
        </GlassCard>
    );
}

// ---------------------------------------------------------------------------
// Full Report
// ---------------------------------------------------------------------------
function ReportView({ data, report }: { data: SimulationDetail; report: SimulationReport }) {
    const navigate = useNavigate();
    const { overview, score_breakdown, strengths, improvement_areas, question_analysis, practice_focus } = report;

    const scoreLabels: Record<keyof typeof score_breakdown, string> = {
        communication: 'Communication',
        role_depth: 'Role Depth',
        problem_solving: 'Problem Solving',
        behavioral_strength: 'Behavioural Strength',
    };

    const elapsedMins = Math.round((data.time_used_seconds ?? 0) / 60);

    return (
        <div className="min-h-screen bg-[#0B0B0F] text-white">
            {/* Header */}
            <header className="sticky top-0 z-10 bg-[#0B0B0F]/90 backdrop-blur border-b border-white/5 px-6 py-4 flex items-center gap-4">
                <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors group">
                    <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-white/10">
                        <RiArrowLeftLine />
                    </div>
                </button>
                <div className="flex-1 min-w-0">
                    <h1 className="font-semibold text-white truncate">{data.job_title}</h1>
                    <p className="text-xs text-gray-500">
                        {data.company_name && `${data.company_name} · `}
                        {data.interview_stage} · {data.persona?.name} · {elapsedMins}m
                    </p>
                </div>
            </header>

            <div className="max-w-[1400px] mx-auto px-4 pt-8 pb-24 grid grid-cols-1 lg:grid-cols-3 gap-8 content-start items-start">

                {/* ── Left Column: Analysis ── */}
                <div className="lg:col-span-2 space-y-8">
                    {/* ── 1. Verdict Banner ── */}
                    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
                        <GlassCard className="p-8 flex flex-col md:flex-row items-center gap-8">
                            <ScoreRing score={overview.overall_score} size={128} strokeWidth={10} />
                            <div className="flex-1 text-center md:text-left space-y-3">
                                <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-sm font-semibold ${verdictStyle(overview.verdict)}`}>
                                    {overview.verdict}
                                </div>
                                <div className="flex items-center gap-2 text-gray-500 text-sm">
                                    <span>Confidence:</span>
                                    <span className="text-white font-medium">{overview.confidence_level}</span>
                                </div>
                                <p className="text-gray-300 leading-relaxed text-lg">{overview.summary}</p>
                            </div>
                        </GlassCard>
                    </motion.div>

                    {/* ── 2. Score Breakdown ── */}
                    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}>
                        <GlassCard className="p-6 space-y-5">
                            <div className="flex items-center gap-2 text-white font-semibold">
                                <RiBarChartLine className="text-gray-400" />
                                Score Breakdown
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4">
                                {(Object.keys(score_breakdown) as Array<keyof typeof score_breakdown>).map(key => (
                                    <ScoreBar key={key} label={scoreLabels[key]} value={score_breakdown[key]} />
                                ))}
                            </div>
                        </GlassCard>
                    </motion.div>

                    {/* ── 3. Strengths ── */}
                    {strengths?.length > 0 && (
                        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.15 }}>
                            <GlassCard className="p-6 space-y-6">
                                <div className="flex items-center gap-2 text-white font-semibold">
                                    <RiCheckLine className="text-green-400" />
                                    Strengths
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {strengths.map((s, i) => (
                                        <div key={i} className="flex gap-3 p-4 rounded-xl bg-green-500/5 border border-green-500/10 transition-all hover:bg-green-500/10 group">
                                            <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center shrink-0 mt-0.5 group-hover:scale-110 transition-transform">
                                                <RiCheckLine className="text-green-400" />
                                            </div>
                                            <div>
                                                <div className="font-semibold text-white text-sm tracking-tight">{s.title}</div>
                                                <div className="text-gray-400 text-xs mt-1 leading-relaxed">{s.description}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </GlassCard>
                        </motion.div>
                    )}

                    {/* ── 4. Improvement Areas ── */}
                    {improvement_areas?.length > 0 && (
                        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.2 }}>
                            <GlassCard className="p-6 space-y-6">
                                <div className="flex items-center gap-2 text-white font-semibold">
                                    <RiAlertLine className="text-yellow-400" />
                                    Areas to Improve
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {improvement_areas.map((area, i) => (
                                        <div key={i} className="p-4 rounded-xl bg-yellow-500/5 border border-yellow-500/10 flex flex-col transition-all hover:bg-yellow-500/10 group">
                                            <div className="flex items-center gap-2 mb-3">
                                                <div className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse shadow-[0_0_8px_rgba(250,204,21,0.4)]" />
                                                <div className="font-semibold text-white text-sm tracking-tight">{area.area}</div>
                                            </div>
                                            <div className="text-gray-400 text-xs leading-relaxed mb-4 flex-1">{area.why_it_matters}</div>
                                            <div className="text-yellow-400/90 text-[10px] font-medium bg-yellow-500/10 rounded-lg px-3 py-2.5 border border-yellow-500/20 leading-snug">
                                                <span className="mr-1.5 shrink-0">💡</span> {area.example_fix}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </GlassCard>
                        </motion.div>
                    )}

                    {/* ── 5. Question Analysis ── */}
                    {question_analysis?.length > 0 && (
                        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.25 }}>
                            <GlassCard className="p-6 space-y-4">
                                <div className="flex items-center gap-2 text-white font-semibold">
                                    <RiBookOpenLine className="text-gray-400" />
                                    Question Analysis
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {question_analysis.map((q, i) => (
                                        <div key={i} className="p-4 rounded-xl bg-white/3 border border-white/5 space-y-3">
                                            <p className="text-sm font-medium text-white line-clamp-2">Q{i + 1}: {q.question_summary}</p>
                                            <p className="text-xs text-gray-400 leading-relaxed line-clamp-3">{q.performance_summary}</p>
                                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-gray-500 pt-2 border-t border-white/5">
                                                <span>Depth: <span className="text-white font-medium">{q.scores.technical_or_role_depth}%</span></span>
                                                <span>Comm: <span className="text-white font-medium">{q.scores.communication}%</span></span>
                                                <span>Conf: <span className="text-white font-medium">{q.scores.confidence}%</span></span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </GlassCard>
                        </motion.div>
                    )}

                    {/* ── 6. Practice Focus ── */}
                    {practice_focus && (
                        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.3 }}>
                            <GlassCard className="p-6 space-y-4 border-white/20">
                                <div className="flex items-center gap-2 text-white font-semibold">
                                    <RiRadarLine className="text-blue-400" />
                                    Next Steps & Practice Focus
                                </div>
                                <div className="grid md:grid-cols-2 gap-6">
                                    <ul className="space-y-2">
                                        {practice_focus.priority.map((item, i) => (
                                            <li key={i} className="flex gap-3 text-sm text-gray-300">
                                                <span className="text-blue-400 mt-0.5 shrink-0">→</span>
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="text-sm text-gray-400 bg-blue-500/5 border border-blue-500/10 rounded-xl px-4 py-3 flex items-center italic">
                                        "{practice_focus.action}"
                                    </div>
                                </div>
                            </GlassCard>
                        </motion.div>
                    )}

                    {/* ── CTA ── */}
                    <div className="flex gap-4 pt-4">
                        <GlassButton onClick={() => navigate('/simulation-setup')} className="flex-1 py-4 text-lg">
                            Practice Again
                        </GlassButton>
                        <GlassButton onClick={() => navigate('/dashboard')} variant="secondary" className="flex-1 py-4 text-lg">
                            Dashboard
                        </GlassButton>
                    </div>
                </div>

                {/* ── Right Column: Transcript ── */}
                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    className="lg:col-span-1"
                >
                    <TranscriptSection transcript={data.transcript ?? []} />
                </motion.div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Page entry — polling wrapper
// ---------------------------------------------------------------------------
export default function SimulationResultPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState<SimulationDetail | null>(null);
    const [error, setError] = useState<string | null>(null);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        if (!id) return;

        const poll = async () => {
            try {
                const result = await simulationService.getSimulation(id);
                setData(result);
                if (result.analysis_status === 'completed' || result.analysis_status === 'failed') {
                    if (pollRef.current) clearInterval(pollRef.current);
                }
            } catch (e: any) {
                setError(e.message ?? 'Failed to load results.');
                if (pollRef.current) clearInterval(pollRef.current);
            }
        };

        poll(); // immediate first fetch
        pollRef.current = setInterval(poll, 3000);
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, [id]);

    if (error) {
        return (
            <div className="min-h-screen bg-[#0B0B0F] text-white flex items-center justify-center flex-col gap-4">
                <RiAlertLine className="text-4xl text-red-400" />
                <p className="text-gray-400">{error}</p>
                <GlassButton onClick={() => navigate('/dashboard')}>Back to Dashboard</GlassButton>
            </div>
        );
    }

    if (!data || data.analysis_status !== 'completed' || !data.report) {
        return (
            <div className="min-h-screen bg-[#0B0B0F] text-white">
                <header className="px-6 py-4 border-b border-white/5 flex items-center gap-3">
                    <button onClick={() => navigate('/dashboard')} className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors">
                        <RiArrowLeftLine />
                    </button>
                    <span className="text-sm text-gray-400">Simulation Results</span>
                </header>
                <AnalysisLoading />
            </div>
        );
    }

    return <ReportView data={data} report={data.report} />;
}
